import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box, Button, Chip, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import CourseLangLogo from "../components/CourseLangLogo";
import { EmptyState, LoadingBlock, PALETTE } from "../components/shared";
import { fetchTestSubmissions, getAttemptResult } from "../hooks/useSkillTests";
import { resolvePassMeta } from "../utils/skillTestPass";
import SkillTestResultView from "./SkillTestResultView";

type Props = {
  testId: string;
  studentId: string;
  testMeta?: any;
  onBack: () => void;
  onOpenTest?: (testId: string) => void;
  backLabel?: string;
};

export default function SkillTestSubmissionsView({
  testId,
  studentId,
  testMeta,
  onBack,
  onOpenTest,
  backLabel = "Back",
}: Props) {
  const [test, setTest] = useState<any>(testMeta ?? null);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [attemptResult, setAttemptResult] = useState<any>(null);
  const [loadingResult, setLoadingResult] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetchTestSubmissions(studentId, testId);
        if (!cancelled) {
          setTest(res.test ?? testMeta ?? { id: testId });
          setSubmissions(res.data ?? []);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [studentId, testId, testMeta]);

  const openAttemptResult = async (attemptId: string) => {
    setLoadingResult(true);
    try {
      const res = await getAttemptResult(attemptId);
      setAttemptResult(res);
    } finally {
      setLoadingResult(false);
    }
  };

  if (loadingResult) return <LoadingBlock message="Loading submission report..." />;

  if (attemptResult) {
    return (
      <SkillTestResultView
        result={attemptResult}
        testMeta={test ?? attemptResult.test}
        onBack={() => setAttemptResult(null)}
        backLabel="Back to Submissions"
      />
    );
  }

  const displayTest = test ?? { id: testId };
  const attempts = displayTest.attempts ?? submissions.length;

  return (
    <Box sx={{ fontSize: 13, maxWidth: 1320, mx: "auto" }}>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ textTransform: "none", mb: 2, fontSize: 12 }}>
        {backLabel}
      </Button>
      <Paper sx={{ p: 2, mb: 2, borderRadius: "12px", border: `1px solid ${PALETTE.border}` }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <CourseLangLogo language={displayTest.programmingLanguage} category={displayTest.category} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 16, color: "primary.main" }}>{displayTest.title ?? "Skill Test"}</Typography>
            <Typography sx={{ fontSize: 12, color: PALETTE.textSecondary }}>
              {submissions.length} submission{submissions.length !== 1 ? "s" : ""}
            </Typography>
          </Box>
          {onOpenTest && (
            <Button
              variant="contained"
              size="small"
              onClick={() => onOpenTest(testId)}
              sx={{ textTransform: "none", fontSize: 12, borderRadius: "8px" }}
            >
              {attempts > 0 ? "Retake Test" : "Start Test"}
            </Button>
          )}
        </Stack>
      </Paper>
      {loading ? (
        <LoadingBlock message="Loading submissions..." />
      ) : submissions.length === 0 ? (
        <EmptyState message="No submissions yet for this test." />
      ) : (
        <SubmissionsTable submissions={submissions} onView={openAttemptResult} />
      )}
    </Box>
  );
}

function isAttemptPassed(s: any): boolean {
  return resolvePassMeta(s).passed;
}

function SubmissionsTable({ submissions, onView }: {
  submissions: any[]; onView: (attemptId: string) => void;
}) {
  const fmtDate = (d?: string) => d ? new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
  const fmtTime = (s?: number) => s != null ? `${Math.floor(s / 60)}m ${s % 60}s` : "—";

  return (
    <TableContainer component={Paper} sx={{ borderRadius: "12px", border: `1px solid ${PALETTE.border}` }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: PALETTE.bgLight }}>
            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Submitted</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Score</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Result</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Time</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Percentile</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {submissions.map((s: any) => {
            const totalMarks = s.totalMarks || s.testMeta?.totalMarks || s.test?.totalMarks || 0;
            const passed = isAttemptPassed(s);
            return (
              <TableRow key={s.id} hover sx={{ "&:last-child td": { border: 0 } }}>
                <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>{fmtDate(s.submittedAt)}</TableCell>
                <TableCell sx={{ fontSize: 12, fontWeight: 700 }}>{s.score ?? 0} / {totalMarks}</TableCell>
                <TableCell>
                  <Chip size="small" label={passed ? "Passed" : "Failed"}
                    sx={{ fontSize: 10, height: 20, bgcolor: passed ? "#f0fdf4" : "#fef2f2", color: passed ? "#16a34a" : "#dc2626" }} />
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>{fmtTime(s.timeTakenSeconds)}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{s.percentile ?? 0}th</TableCell>
                <TableCell align="right">
                  <Button size="small" variant="outlined" startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                    onClick={() => onView(s.id)}
                    sx={{ textTransform: "none", fontSize: 11, height: 28, borderRadius: "8px" }}>
                    View Report
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
